import { useState, useEffect } from 'react';

/**
 * Service for fetching and managing active listings data
 * Fetches from our backend which queries Spark for:
 * `/Property?$filter=StandardStatus eq 'Active'`
 */

let listingsCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
let dataLoadPromise = null;
let isDataLoading = false;

/**
 * Calculate Days on Market from onMarketDate
 * @param {string|Date} onMarketDate - The date the listing went on market
 * @returns {number} - Days on market (0 if date is invalid or in the future)
 */
export function calculateDaysOnMarket(onMarketDate) {
  if (!onMarketDate) return null;
  
  const marketDate = new Date(onMarketDate);
  const today = new Date();
  
  // Reset time to midnight for accurate day calculation
  today.setHours(0, 0, 0, 0);
  marketDate.setHours(0, 0, 0, 0);
  
  // If date is invalid or in the future, return 0
  if (isNaN(marketDate.getTime()) || marketDate > today) {
    return 0;
  }
  
  const diffTime = today - marketDate;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  return Math.max(0, diffDays);
}

/**
 * Transform raw listing data from API into a consistent format
 * @param {Array} rawData - Raw listing data from API
 * @returns {Array} - Transformed listing data
 */
const transformListingData = (rawData) => {
  if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
    return [];
  }

  const listings = [];

  rawData.forEach((listing) => {
    // All listings from API are already Active (filtered at API level)

    // Extract photo URLs from media array
    const photoUrls = listing.media && Array.isArray(listing.media)
      ? listing.media
          .filter(media => media.MediaCategory === 'Photo')
          .sort((a, b) => (a.Order || 0) - (b.Order || 0))
          .map(media => media.MediaURL || media.Url || '')
          .filter(url => url)
      : [];

    // Prefer API-provided Days on Market when available; fall back to computing from onMarketDate
    const apiDaysOnMarket = typeof listing.daysOnMarket === 'number' && Number.isFinite(listing.daysOnMarket)
      ? listing.daysOnMarket
      : null;
    const daysOnMarket = apiDaysOnMarket != null ? apiDaysOnMarket : calculateDaysOnMarket(listing.onMarketDate);

    // Build agent name
    const agentFirstName = listing.listAgentFirstName || listing.raw?.ListAgentFirstName || '';
    const agentLastName = listing.listAgentLastName || listing.raw?.ListAgentLastName || '';
    const agentName = agentFirstName && agentLastName
      ? `${agentFirstName} ${agentLastName}`
      : agentFirstName || agentLastName || '';

    listings.push({
      id: listing.listingKey || listing.listingId,
      listingKey: listing.listingKey,
      listingId: listing.listingId || '',
      address: listing.address || listing.unparsedAddress || '',
      unparsedAddress: listing.unparsedAddress || listing.address || '',
      price: listing.price || 0,
      // Keep nulls (don't coerce to 0) so we don't show fake "0 bed" for land listings
      beds: listing.beds ?? null,
      baths: listing.baths ?? null,
      sqft: listing.sqft ?? null,
      city: listing.city || '',
      state: listing.state || '',
      postalCode: listing.postalCode || '',
      streetNumber: listing.streetNumber || '',
      streetDirPrefix: listing.streetDirPrefix || '',
      streetName: listing.streetName || '',
      streetSuffix: listing.streetSuffix || '',
      unitNumber: listing.unitNumber || '',
      subdivisionName: listing.subdivisionName || listing.SubdivisionName || '',
      // Keep nulls; do NOT coerce to 0 (0,0 breaks map fitBounds and marker placement)
      latitude: listing.latitude ?? null,
      longitude: listing.longitude ?? null,
      photo: photoUrls[0] || '',
      photos: photoUrls,
      agentName: agentName,
      listAgentKey: listing.listAgentKey || listing.raw?.ListAgentKey || null,
      status: listing.status || listing.StandardStatus || 'Active',
      onMarketDate: listing.onMarketDate || null,
      daysOnMarket: daysOnMarket,
      propertyType: listing.propertyType || '',
      propertySubType: listing.propertySubType || '',
      yearBuilt: listing.yearBuilt || null,
      lotSizeAcres: listing.lotSizeAcres || null,
      garageSpaces: listing.garageSpaces || null,
      raw: listing.raw || listing
    });
  });

  // Sort by Days on Market (newest first = lowest days on market first)
  return listings.sort((a, b) => {
    const aDays = a.daysOnMarket ?? Infinity;
    const bDays = b.daysOnMarket ?? Infinity;
    
    // If both have valid days on market, sort ascending (newest first)
    if (aDays !== Infinity && bDays !== Infinity) {
      return aDays - bDays;
    }
    
    // If one has valid days and the other doesn't, prioritize the one with valid days
    if (aDays !== Infinity && bDays === Infinity) {
      return -1;
    }
    if (aDays === Infinity && bDays !== Infinity) {
      return 1;
    }
    
    // If neither has valid days, maintain original order
    return 0;
  });
};

/**
 * Load active listings data from API
 */
export async function loadActiveListingsData() {
  if (isDataLoading) {
    return dataLoadPromise;
  }

  // Check cache first
  if (listingsCache && cacheTimestamp) {
    const cacheAge = Date.now() - cacheTimestamp;
    if (cacheAge < CACHE_DURATION) {
      return listingsCache;
    }
  }

  isDataLoading = true;
  dataLoadPromise = (async () => {
    try {
      let baseUrl = 'http://localhost:4000';

      // Use network IP when accessing from mobile device
      if (window.location.hostname === '192.168.4.177') {
        baseUrl = 'http://192.168.4.177:4000';
      } else if (import.meta.env.MODE === 'production' || import.meta.env.MODE === 'staging') {
        baseUrl = 'https://rmkp-open-houses.onrender.com';
      }

      const url = `${baseUrl}/api/listings`;
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        credentials: import.meta.env.MODE === 'production' ? 'include' : 'same-origin'
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${res.status}`);
      }

      const data = await res.json();
      const rawListings = data.value || [];
      
      const transformedListings = transformListingData(rawListings);
      
      // Update cache
      listingsCache = transformedListings;
      cacheTimestamp = Date.now();
      
      return transformedListings;
    } catch (error) {
      console.error('Error loading active listings:', error);
      throw error;
    } finally {
      isDataLoading = false;
      dataLoadPromise = null;
    }
  })();

  return dataLoadPromise;
}

/**
 * React hook for using active listings data
 * Similar to useOpenHouses hook
 */
export function useActiveListings() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await loadActiveListingsData();
        
        if (isMounted) {
          setListings(data);
          setLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setError(err);
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, []);

  return { listings, loading, error };
}

